import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Copy, ShareNetwork, WhatsappLogo, XLogo, Sparkle, Users, TrendUp, Star } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InviteFriends = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${BACKEND_URL}/api/referrals/my-code`, h),
      axios.get(`${BACKEND_URL}/api/referrals/my-stats`, h),
    ]).then(([c, s]) => {
      setData(c.data);
      setStats(s.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Screen msg="Loading your invite kit…" />;
  if (!data) return <Screen msg="Please sign in to grab your invite code." />;

  const copyLink = () => {
    navigator.clipboard.writeText(data.register_url);
    toast.success('Invite link copied');
  };
  const copyCode = () => {
    navigator.clipboard.writeText(data.code);
    toast.success(`Code "${data.code}" copied`);
  };
  const shareMessage = `Just discovered Fanzingy — the marketplace where fans co-own movies. Join with my code ${data.code} and we both get bonus Zinglets to back our first film together. ${data.register_url}`;
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`, '_blank');
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Fanzingy', text: shareMessage, url: data.register_url }); }
      catch (_) {}
    } else copyLink();
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid="invite-page">
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5" data-testid="invite-back">
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-base font-bold">Invite friends</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 mb-4">
          <Sparkle size={14} weight="fill" className="text-[#facc15]" />
          <span className="text-[11px] uppercase tracking-wider font-bold">Earn together</span>
        </div>
        <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-3" style={{ letterSpacing: '-0.03em' }} data-testid="invite-title">
          Bring a friend.{' '}
          <span style={{ background: 'linear-gradient(90deg,#f472b6,#c084fc,#22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Both back a movie.
          </span>
        </h2>
        <p className="text-white/65 max-w-2xl mb-8 font-light text-base">
          You and the friend you invite each earn <strong className="text-[#facc15]">+1 Zinglet</strong> the moment they sign up. When they make their first investment, you earn <strong className="text-[#facc15]">+5 more</strong>. Zinglets are platform credits applied at checkout (1 Zinglet = ₹100).
        </p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="invite-stats">
          <StatTile testid="stat-invited" icon={Users} label="Invited" value={stats?.invited ?? 0} tone="text-[#c084fc] from-[#c084fc]/15 to-[#7e22ce]/8 border-[#c084fc]/30" />
          <StatTile testid="stat-signed-up" icon={Sparkle} label="Signed up" value={stats?.signed_up ?? 0} tone="text-[#f472b6] from-[#f472b6]/15 to-rose-700/8 border-[#f472b6]/30" />
          <StatTile testid="stat-invested" icon={TrendUp} label="Invested" value={stats?.invested ?? 0} tone="text-emerald-300 from-emerald-500/15 to-emerald-700/8 border-emerald-400/30" />
          <StatTile testid="stat-zinglets" icon={Star} label="Zinglets earned" value={stats?.zinglets_earned ?? 0} tone="text-[#facc15] from-[#facc15]/15 to-amber-700/8 border-[#facc15]/30" />
        </div>

        {/* Code + share */}
        <div className="bg-gradient-to-br from-white/[0.07] via-white/[0.04] to-transparent border border-white/15 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="text-xs uppercase tracking-wider text-white/70 font-bold mb-2">Your invite code</div>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="text-4xl sm:text-5xl font-extrabold font-mono tracking-tight bg-black/40 border border-white/15 rounded-xl px-5 py-3" data-testid="invite-code-text">
              {data.code}
            </div>
            <button onClick={copyCode} data-testid="copy-code-btn" className="text-sm bg-white/10 hover:bg-white/15 border border-white/15 rounded-full px-4 py-2 flex items-center gap-1.5">
              <Copy size={14} weight="bold" /> Copy code
            </button>
          </div>

          <div className="text-xs uppercase tracking-wider text-white/70 font-bold mb-2">Or share the link</div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <code className="text-xs bg-black/40 border border-white/10 rounded-lg px-3 py-2 break-all flex-1 min-w-0" data-testid="invite-link-text">{data.register_url}</code>
            <button onClick={copyLink} data-testid="copy-link-btn" className="text-xs bg-white text-black font-bold rounded-full px-4 py-2 hover:scale-105 transition-transform">Copy link</button>
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-white/8">
            <ShareBtn testid="share-whatsapp" onClick={shareWA} icon={WhatsappLogo} label="WhatsApp" colour="bg-[#25D366]/20 border-[#25D366]/40 text-[#25D366]" />
            <ShareBtn testid="share-x" onClick={shareX} icon={XLogo} label="X / Twitter" colour="bg-white/10 border-white/20 text-white" />
            <ShareBtn testid="share-native" onClick={shareNative} icon={ShareNetwork} label="Share…" colour="bg-[#c084fc]/15 border-[#c084fc]/40 text-[#c084fc]" />
          </div>
        </div>

        {/* Recent referrals */}
        {(stats?.recent || []).length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="recent-referrals">
            <h3 className="text-xs uppercase tracking-wider text-white/70 font-bold mb-3">Recent invites</h3>
            <div className="space-y-2">
              {stats.recent.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-black/30 border border-white/8 rounded-lg px-3 py-2 text-xs">
                  <span className="text-white/70 truncate">{r.referee_email || 'Unknown'}</span>
                  <span className={`px-2 py-0.5 rounded-full uppercase tracking-wider font-bold text-[10px] ${
                    r.status === 'invested' ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-300' :
                    r.status === 'signed_up' ? 'bg-[#c084fc]/15 border border-[#c084fc]/30 text-[#c084fc]' :
                    'bg-white/8 border border-white/15 text-white/60'
                  }`}>{r.status.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatTile = ({ testid, icon: Icon, label, value, tone }) => (
  <div className={`relative overflow-hidden rounded-xl p-4 bg-gradient-to-br ${tone} border backdrop-blur-md`} data-testid={testid}>
    <Icon size={22} weight="bold" className="mb-2 opacity-90" />
    <div className="text-3xl font-extrabold tabular-nums leading-none mb-1" style={{ letterSpacing: '-0.03em' }}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">{label}</div>
  </div>
);

const ShareBtn = ({ testid, onClick, icon: Icon, label, colour }) => (
  <button data-testid={testid} onClick={onClick} className={`text-xs px-3 py-2 rounded-full flex items-center gap-1.5 border ${colour} hover:scale-105 transition-transform font-bold`}>
    <Icon size={14} weight="bold" /> {label}
  </button>
);

const Screen = ({ msg }) => (
  <div className="min-h-screen flex items-center justify-center text-white/70 bg-[#080414]">{msg}</div>
);

export default InviteFriends;
